/**
 * Pure pager math for the `status_strip` dashboard widget's server-side
 * pagination. The widget fetches one page at a time (height-adaptive page size);
 * this module derives the 1-based "start–end of N" range label, the prev/next
 * availability, and the page-step offsets from the current offset + page
 * contents + server total. Extracted from StatusStripWidget so this logic can be
 * unit-tested independently of the React component, mirroring the co-located
 * pure-logic modules of the sibling widgets (statusTileState, statusMatrixState,
 * recordListWidgetFit).
 */

export interface StripPagerInput {
    /** Server-side offset of the first row currently shown (0-based). */
    offset: number;
    /** Number of rows actually rendered on the current page. */
    recordCount: number;
    /** Server total row count across all pages. */
    totalCount: number;
}

export interface StripPagerRange {
    /** 1-based index of the first row shown (0 when the total is 0). */
    rangeStart: number;
    /** 1-based index of the last row shown, bounded by the real page contents
     *  so a short last page reports its true last row. */
    rangeEnd: number;
    /** A previous page exists (the current page is not the first). */
    hasPrev: boolean;
    /** A next page exists (more rows follow the current page). */
    hasNext: boolean;
    /** More than one page exists → the prev/next controls should be shown. */
    multiPage: boolean;
}

/**
 * Derive the pager range + control state for the current page view. The caller
 * renders the footer only when a page has rows, so `rangeStart` follows the
 * component's original `totalCount === 0 ? 0 : offset + 1` convention.
 */
export const computeStripPagerRange = ({
    offset,
    recordCount,
    totalCount,
}: StripPagerInput): StripPagerRange => {
    const rangeStart = totalCount === 0 ? 0 : offset + 1;
    const rangeEnd = offset + recordCount;
    const hasPrev = offset > 0;
    const hasNext = rangeEnd < totalCount;
    const multiPage = totalCount > recordCount || offset > 0;
    return { rangeStart, rangeEnd, hasPrev, hasNext, multiPage };
};

/** Offset of the previous page, never below 0. */
export const prevPageOffset = (offset: number, pageSize: number): number =>
    Math.max(0, offset - pageSize);

/** Offset of the next page. */
export const nextPageOffset = (offset: number, pageSize: number): number =>
    offset + pageSize;
